import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, CircularProgress, Alert, Card, CardContent, Avatar, Button, IconButton, CardMedia, Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { parseFirestoreDate } from '../../lib/dateUtils';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import GroupsIcon from '@mui/icons-material/Groups';
import { apiClient } from '../../lib/apiClient';
import { formatDate } from '../../lib/dateUtils';
import { isYouTubeUrl, getYouTubeThumbnail, getYouTubeEmbedUrl } from '../../lib/videoUtils';
import NoChurchSelected from '../../components/common/NoChurchSelected';

function UserDashboard({ user }) {
  const navigate = useNavigate();
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bannerImages, setBannerImages] = useState([]);
  const [quoteOfTheDay, setQuoteOfTheDay] = useState('');
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [recentSermons, setRecentSermons] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);

  useEffect(() => {
    const loadSelectedChurch = async () => {
      try {
        // Get user's churches first to validate
        const myChurches = await apiClient.getMyChurches(user.id);
        
        if (myChurches.length === 0) {
          // Clear invalid selected church if user has no churches
          localStorage.removeItem('selectedChurch');
          setError('Please join a church first');
          setLoading(false);
          return;
        }

        // Try to get selected church from localStorage and validate it
        const stored = localStorage.getItem('selectedChurch');
        let currentChurch;
        if (stored) {
          const storedChurch = JSON.parse(stored);
          // Verify the stored church is in the user's churches
          const isValidChurch = myChurches.some(c => c.id === storedChurch.id);
          if (isValidChurch) {
            currentChurch = storedChurch;
          } else {
            // Clear invalid church and select first available
            localStorage.removeItem('selectedChurch');
            currentChurch = myChurches[0];
            localStorage.setItem('selectedChurch', JSON.stringify(currentChurch));
            window.dispatchEvent(new Event('churchSelected'));
          }
        } else {
          // No stored church, select first one
          currentChurch = myChurches[0];
          localStorage.setItem('selectedChurch', JSON.stringify(currentChurch));
          window.dispatchEvent(new Event('churchSelected'));
        }

        setSelectedChurch(currentChurch);

        // Load user display settings
        try {
          const settings = await apiClient.getUserDisplaySettings(currentChurch.id);
          setBannerImages(settings.bannerImages || []);
          const quote = settings.quoteOfTheDay || '';
          setQuoteOfTheDay(quote);
          
          // Show quote popup if it exists and hasn't been shown in this session
          if (quote && !sessionStorage.getItem('quoteShownInSession')) {
            setQuoteDialogOpen(true);
          }
        } catch (err) {
          console.error('Error loading user display settings:', err);
          // Don't fail the whole page if settings fail to load
        }

        // Load recent sermons (limit to 10 most recent)
        try {
          const sermons = await apiClient.getSermons(currentChurch.id);
          // Sort by date (most recent first) and take first 10
          const sortedSermons = [...sermons].sort((a, b) => {
            const aTime = a.sermonDate?.seconds || a.sermonDate?._seconds || 0;
            const bTime = b.sermonDate?.seconds || b.sermonDate?._seconds || 0;
            return bTime - aTime;
          }).slice(0, 10);
          setRecentSermons(sortedSermons);
        } catch (err) {
          console.error('Error loading recent sermons:', err);
          // Don't fail the whole page if sermons fail to load
        }

        // Load recent community posts (limit to 5 most recent)
        try {
          const posts = await apiClient.getPosts(currentChurch.id);
          // Posts are already sorted by createdAt desc from API, take first 5
          setRecentPosts(posts.slice(0, 5));
        } catch (err) {
          console.error('Error loading recent posts:', err);
          // Don't fail the whole page if posts fail to load
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadSelectedChurch();
    }
  }, [user]);

  // Auto-rotate banner images
  useEffect(() => {
    if (bannerImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentBannerIndex((prevIndex) => (prevIndex + 1) % bannerImages.length);
      }, 5000); // Change image every 5 seconds

      return () => clearInterval(interval);
    }
  }, [bannerImages.length]);

  const handlePreviousBanner = () => {
    setCurrentBannerIndex((prevIndex) => 
      prevIndex === 0 ? bannerImages.length - 1 : prevIndex - 1
    );
  };

  const handleNextBanner = () => {
    setCurrentBannerIndex((prevIndex) => 
      (prevIndex + 1) % bannerImages.length
    );
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const handleCloseQuoteDialog = () => {
    setQuoteDialogOpen(false);
    sessionStorage.setItem('quoteShownInSession', 'true');
  };

  const formatPostTimestamp = (timestamp) => {
    const date = parseFirestoreDate(timestamp);
    if (!date) return 'N/A';
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && error === 'Please join a church first') {
    return <NoChurchSelected user={user} />;
  }

  if (error || !selectedChurch) {
    return (
      <Box>
        <Alert 
          severity={error ? 'error' : 'info'}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/church-user/profile')}>
              Select Church
            </Button>
          }
        >
          {error || 'Please select a church to view your dashboard'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
        Welcome, {user.name}
      </Typography>
      
      {/* Banner Slider */}
      {bannerImages.length > 0 && (
        <Box sx={{ position: 'relative', mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Box
            component="img"
            src={bannerImages[currentBannerIndex]}
            alt={`Banner ${currentBannerIndex + 1}`}
            sx={{
              width: '100%',
              height: { xs: 200, sm: 300, md: 400 },
              objectFit: 'cover',
              display: 'block',
            }}
          />
          {bannerImages.length > 1 && (
            <>
              <IconButton
                onClick={handlePreviousBanner}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={handleNextBanner}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
              {/* Dots indicator */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 1,
                }}
              >
                {bannerImages.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentBannerIndex(index)}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: index === currentBannerIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s',
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Quote of the Day */}
      {quoteOfTheDay && (
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            background: 'linear-gradient(135deg, #7b2514 0%, #dc3f3f 100%)',
            color: 'white',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              fontWeight: 600,
              mb: 1,
              textTransform: 'uppercase',
              letterSpacing: 1,
            }}
          >
            Quote of the Day
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', sm: '1.125rem' },
              fontStyle: 'italic',
              lineHeight: 1.6,
            }}
          >
            "{quoteOfTheDay}"
          </Typography>
        </Paper>
      )}

      <Card sx={{ mt: 2, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
            <Avatar src={selectedChurch.logoUrl} sx={{ width: { xs: 60, sm: 80 }, height: { xs: 60, sm: 80 } }}>
              {selectedChurch.name?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>{selectedChurch.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {selectedChurch.address?.address}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {selectedChurch.address?.city}, {selectedChurch.address?.state} {selectedChurch.address?.pincode}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Quick Action Buttons */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: 2, mb: 3, justifyContent: 'space-evenly' }}>
        <Grid item xs={6} sm={3}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                cursor: 'pointer',
                borderRadius: 3,
              
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                },
                textAlign: 'center',
                p: 2.5,
                backgroundColor: 'white',
                aspectRatio: '1',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => navigate('/church-user/prayer-requests')}
            >
              <FavoriteIcon
                sx={{
                  fontSize: { xs: 30, sm: 42 },
                  color: '#d4af39',
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                color: '#4A5568',
                fontWeight: 700,
              }}
            >
              Prayer
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                cursor: checkingIn ? 'wait' : 'pointer',
                borderRadius: 3,
                transition: 'transform 0.2s, box-shadow 0.2s',
                opacity: checkingIn ? 0.6 : 1,
                '&:hover': {
                  transform: checkingIn ? 'none' : 'translateY(-2px)',
                },
                textAlign: 'center',
                p: 2.5,
                backgroundColor: 'white',
                aspectRatio: '1',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => {
                if (!selectedChurch) return;
                setCheckInDialogOpen(true);
              }}
            >
              {checkingIn ? (
                <CircularProgress size={42} sx={{ color: '#eba83c' }} />
              ) : (
                <CheckCircleIcon
                  sx={{
                    fontSize: { xs: 30, sm: 42 },
                    color: '#d4af39',
                  }}
                />
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                color: '#4A5568',
                fontWeight: 700,
              }}
            >
              Check-in
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                cursor: 'pointer',
                borderRadius: 3,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                },
                textAlign: 'center',
                p: 2.5,
                backgroundColor: 'white',
                aspectRatio: '1',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => navigate('/church-user/give')}
            >
              <VolunteerActivismIcon
                sx={{
                  fontSize: { xs: 30, sm: 42 },
                  color: '#d4af39',
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                color: '#4A5568',
                fontWeight: 700,
              }}
            >
              Give
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                cursor: 'pointer',
                borderRadius: 3,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                },
                textAlign: 'center',
                p: 1,
                backgroundColor: 'white',
                aspectRatio: '1',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => navigate('/church-user/community')}
            >
              <GroupsIcon
                sx={{
                  fontSize: { xs: 30, sm: 42 },
                  color: '#d4af39',
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                color: '#4A5568',
                fontWeight: 700,
              }}
            >
              Community
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Recent Sermons Section */}
      {recentSermons.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
              Recent Sermons
            </Typography>
            <Button
              variant="text"
              onClick={() => navigate('/church-user/sermons')}
              sx={{ fontSize: { xs: '0.775rem', sm: '1rem' }, color: '#d4af39' }}
            >
              See All
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              pb: 2,
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.3)',
                borderRadius: 4,
              },
            }}
          >
            {recentSermons.map((sermon) => {
              let thumbnailUrl = null;
              let embedUrl = null;
              if (sermon.videoUrl) {
                if (isYouTubeUrl(sermon.videoUrl)) {
                  thumbnailUrl = getYouTubeThumbnail(sermon.videoUrl);
                  embedUrl = getYouTubeEmbedUrl(sermon.videoUrl);
                }
              }
              
              return (
                <Card
                  key={sermon.id}
                  sx={{
                    minWidth: { xs: 200, sm: 240 },
                    maxWidth: { xs: 200, sm: 240 },
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                  }}
                  onClick={() => navigate('/church-user/sermons')}
                >
                  <Box sx={{ position: 'relative', backgroundColor: '#e0e0e0', overflow: 'hidden', borderRadius: '8px 8px 0 0' }}>
                    {sermon.videoUrl && isYouTubeUrl(sermon.videoUrl) && embedUrl ? (
                      <Box
                        sx={{
                          position: 'relative',
                          paddingBottom: '56.25%', // 16:9 aspect ratio
                          height: 0,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          component="iframe"
                          src={embedUrl}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 0,
                          }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </Box>
                    ) : sermon.videoUrl ? (
                      <Box
                        component="video"
                        controls
                        src={sermon.videoUrl}
                        sx={{
                          width: '100%',
                          maxWidth: '100%',
                        }}
                      >
                        Your browser does not support the video tag.
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          paddingBottom: '56.25%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #d4af39 0%, #C4A572 100%)',
                        }}
                      >
                        <PlayArrowIcon sx={{ fontSize: 48, color: 'white', opacity: 0.7, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                      </Box>
                    )}
                  </Box>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {sermon.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                    >
                      {formatDate(sermon.sermonDate)}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Recent Community Posts Section */}
      {recentPosts.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
              Recent Community Posts
            </Typography>
            <Button
              variant="text"
              onClick={() => navigate('/church-user/community')}
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: '#d4af39' }}
            >
              See All
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              maxWidth: { xs: '100%', md: 800 },
            }}
          >
            {recentPosts.map((post) => (
              <Card
                key={post.id}
                sx={{
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                  },
                }}
                onClick={() => navigate('/church-user/community')}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Avatar
                      sx={{
                        bgcolor: '#d4af39',
                        width: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        mr: 1.5,
                      }}
                    >
                      {getInitials(post.userName)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                        }}
                      >
                        {post.userName || 'Unknown User'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                      >
                        {formatPostTimestamp(post.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                      mb: post.imageUrl ? 1.5 : 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: 1.5,
                    }}
                  >
                    {post.content}
                  </Typography>
                  {post.imageUrl && (
                    <CardMedia
                      component="img"
                      image={post.imageUrl}
                      alt="Post"
                      sx={{
                        width: '100%',
                        maxHeight: 300,
                        objectFit: 'contain',
                        borderRadius: 1,
                        mt: 1.5,
                      }}
                    />
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      <Dialog
        open={checkInDialogOpen}
        onClose={() => setCheckInDialogOpen(false)}
        aria-labelledby="check-in-dialog-title"
        aria-describedby="check-in-dialog-description"
      >
        <DialogTitle id="check-in-dialog-title">
          Confirm Check-in
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="check-in-dialog-description">
            Are you sure you want to check in to {selectedChurch?.name}? You can only check in once per day.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckInDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setCheckInDialogOpen(false);
              if (!selectedChurch) return;
              setCheckingIn(true);
              try {
                await apiClient.checkIn(user.id, selectedChurch.id);
                setSnackbar({
                  open: true,
                  message: 'Successfully checked in!',
                  severity: 'success',
                });
              } catch (err) {
                setSnackbar({
                  open: true,
                  message: err.message || 'Failed to check in',
                  severity: 'error',
                });
              } finally {
                setCheckingIn(false);
              }
            }}
            variant="contained"
            sx={{
              backgroundColor: '#d4af39',
              '&:hover': { backgroundColor: '#8B6F47' },
            }}
            disabled={checkingIn}
          >
            {checkingIn ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quote of the Day Dialog */}
      <Dialog
        open={quoteDialogOpen}
        onClose={handleCloseQuoteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #7b2514 0%, #dc3f3f 100%)',
            color: 'white',
            border: '3px solid #ffffff',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: { xs: '1.125rem', sm: '1.25rem' },
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 1,
            pb: 1,
          }}
        >
          Quote of the Day
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: 'white',
              fontSize: { xs: '1rem', sm: '1.125rem' },
              fontStyle: 'italic',
              lineHeight: 1.8,
              pt: 1,
            }}
          >
            "{quoteOfTheDay}"
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={handleCloseQuoteDialog}
            variant="contained"
            sx={{
              backgroundColor: 'white',
              color: '#d4af39',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default UserDashboard;

